"use client";

import { motion } from "framer-motion";
import {
  Database,
  Workflow,
  ShieldCheck,
  Globe2,
  LineChart,
  Code2,
} from "lucide-react";
import { fadeUp, cardSlideUp, staggerContainer } from "@/lib/motion";

const features = [
  {
    icon: Database,
    title: "Premium Datasets",
    description:
      "500+ curated, verified datasets spanning healthcare organizations and adjacent industry verticals, ready to query.",
  },
  {
    icon: Workflow,
    title: "Integrate in minutes",
    description:
      "A clean RESTful API with SDKs for every major language — no bespoke ingestion pipeline required.",
  },
  {
    icon: ShieldCheck,
    title: "Enterprise-grade security",
    description:
      "JWT authentication, per-key rate limiting, and AES-256 encryption on data at rest and in transit.",
  },
  {
    icon: Globe2,
    title: "Global coverage",
    description:
      "179 countries, refreshed in real time, backed by a 99.9% uptime SLA.",
  },
  {
    icon: LineChart,
    title: "Live usage analytics",
    description:
      "Track consumption, monitor spend, and profile query performance from one dashboard.",
  },
  {
    icon: Code2,
    title: "Built for developers first",
    description:
      "An interactive API playground, live documentation, and a free sandbox environment from day one.",
  },
];

export default function Features() {
  return (
    <section className="bg-[#E826290D] py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="max-w-xl text-4xl font-medium tracking-tight text-ink sm:text-5xl"
          >
            Everything you need to ship data products
          </motion.h2>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            custom={1}
            className="flex max-w-sm flex-col items-start gap-4 md:items-end md:text-right"
          >
            <p className="text-sm font-normal text-ink-soft">
              From discovery to integration — we handle the complexity so your team
              can move fast.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="rounded-full bg-brand px-4 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-dark"
            >
              Why AgencyDB
            </motion.button>
          </motion.div>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              variants={cardSlideUp}
              custom={i}
              whileHover={{ y: -6, transition: { duration: 0.25, ease: "easeOut" } }}
              className="flex flex-col rounded-3xl border border-black/10 bg-white p-8 shadow-sm transition-shadow duration-300 hover:shadow-lg"
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                <feature.icon className="h-6 w-6 stroke-[1.5]" />
              </span>
              <div className="mt-16">
                <h3 className="font-sans text-xl font-semibold text-ink">
                  {feature.title}
                </h3>
                <p className="mt-4 font-sans text-sm font-normal leading-relaxed text-ink-soft">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
